import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Popover,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FetchContext } from 'src/context/FetchContext';
import { AuthContext } from 'src/context/AuthContext';
import { API_ROUTES } from 'src/services/constant';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';
const TEAL_MID = '#4BBBC4';

const { PRIVATE_ROUTE } = API_ROUTES;

const getTodayDate = () => new Date(new Date().setHours(0, 0, 0, 0));

export default function NewDailyWorkReport({ open, onClose, defaultDate, onSuccess }) {
  const { authAxios } = useContext(FetchContext);
  const { state: authState } = useContext(AuthContext);
  const currentUser = authState.user;
  const isAdminOrManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [taskCostItems, setTaskCostItems] = useState([]);
  const [dateRange, setDateRange] = useState([
    { startDate: getTodayDate(), endDate: getTodayDate(), key: 'selection' },
  ]);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: currentUser?._id || '',
    job: null,
    task: null,
    dwr_hour: '',
    cost_item: '',
    description: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      const base = defaultDate ? new Date(`${defaultDate}T00:00:00`) : getTodayDate();
      setDateRange([{ startDate: base, endDate: base, key: 'selection' }]);
      setFormData((f) => ({
        ...f,
        employee_id: currentUser?._id || '',
        job: null,
        task: null,
        dwr_hour: '',
        cost_item: '',
        description: '',
      }));
      setFilteredTasks([]);
      setTaskCostItems([]);
      setErrors({});
      setConflicts([]);
      if (isAdminOrManager) fetchEmployees();
      fetchJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDate]);

  const fetchEmployees = async () => {
    try {
      const { data } = await authAxios.get(`${PRIVATE_ROUTE.GET_USER}?page=1&per_page=1000&search=`);
      setEmployees((data.data || []).filter((u) => !u.is_deleted));
    } catch (e) {
      console.error('Failed to fetch employees', e);
    }
  };

  const fetchTaskCostItems = async (taskId) => {
    try {
      const { data } = await authAxios.get(
        `${PRIVATE_ROUTE.GET_TASK_BY_ID}/${taskId}`
      );

      const task = data?.data?.[0];

      const labourCostItems =
        task?.ratesheet_id?.billable_line_items?.labourItem ?? [];

      setTaskCostItems(labourCostItems);
    } catch (e) {
      console.error('Failed to fetch task cost items', e);
      setTaskCostItems([]);
    }
  };

  const fetchJobs = async () => {
    try {
      const { data } = await authAxios.get(`${PRIVATE_ROUTE.GET_JOBALL}`);

      const filtered = (data?.data ?? [])
        .filter(
          (job) =>
            // job.sub_contract === 'NON BILLABLE' &&
            job.is_deleted === false &&
            job.active === true
        )
        .map((job) => ({
          ...job,
          task: (job.task || []).filter(
            (task) =>
              task.active === true && // only active tasks
              // task.is_completed === 0 && // incomplete tasks
              task.is_deleted !== true
            //  && task.ratesheet_name === 'NON BILLABLE'
          ),
        }))
        .filter((job) => job.task.length > 0); // optional: remove jobs with no valid tasks

      setJobs(filtered);
    } catch (e) {
      console.error('Failed to fetch jobs', e);
    }
  };

  const handleJobChange = (selected) => {
    setFormData((f) => ({ ...f, job: selected, task: null, cost_item: '' }));
    setFilteredTasks(selected ? selected.task ?? [] : []);
    setTaskCostItems([]);
    if (errors.job) setErrors((e) => ({ ...e, job: undefined }));
  };

  const handleChange = (field, value) => {
    setFormData((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const handleDeleteConflict = async (recordId) => {
    try {
      await authAxios.post(`${PRIVATE_ROUTE.DELETE_EMPLOYEE_LEAVE}${recordId}`);
      setConflicts((prev) => prev.filter((c) => c._id !== recordId));
    } catch (e) {
      console.error('Failed to delete conflicting leave', e);
    }
  };

  const { startDate, endDate } = dateRange[0];
  const isRange = startDate && endDate && startDate.toDateString() !== endDate.toDateString();
  const dateLabel =
    startDate && endDate
      ? isRange
        ? `${format(startDate, 'MMM dd, yyyy')} – ${format(endDate, 'MMM dd, yyyy')}`
        : format(startDate, 'MMM dd, yyyy')
      : '';

  const validate = () => {
    const newErrors = {};
    if (isAdminOrManager && !formData.employee_id) newErrors.employee_id = 'Employee is required';
    if (!formData.job) newErrors.job = 'Job is required';
    if (!formData.task) newErrors.task = 'Task is required';
    if (!startDate) newErrors.date = 'Date is required';
    if (
      formData.dwr_hour === '' ||
      !Number.isFinite(Number(formData.dwr_hour)) ||
      Number(formData.dwr_hour) <= 0
    ) {
      newErrors.dwr_hour = 'Enter a valid DWR Hour';
    }
    if (!formData.cost_item) newErrors.cost_item = 'Cost item is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      // console.log('form data: ', formData);

      const employeeId =
        typeof formData.employee_id === 'object'
          ? formData.employee_id?._id
          : formData.employee_id;

      const employee =
        employees.find((e) => String(e._id) === String(employeeId)) ||
        (
          String(currentUser?._id) === String(employeeId)
            ? currentUser
            : null
        );

      const userId = employeeId;


      // const employee = formData.employee_id;

      // const userId =
      //   typeof employee === 'string'
      //     ? employee
      //     : employee?._id;

      const taskId = formData.task?._id;

      if (!userId || !taskId) {
        setErrors((prev) => ({
          ...prev,
          submit: !userId
            ? 'Employee information is missing.'
            : 'Task information is missing.',
        }));
        return;
      }

      const employeeName = [
        employee?.first_name,
        employee?.last_name,
      ]
        .filter(Boolean)
        .join(' ');

      // const employeeName =
      //   typeof employee === 'object' && employee
      //     ? [
      //       employee.first_name,
      //       employee.last_name,
      //     ]
      //       .filter(Boolean)
      //       .join(' ')
      //     : '';

      const costItem =
        typeof formData.cost_item === 'string'
          ? formData.cost_item
          : formData.cost_item?.costItem ||
          formData.cost_item?.costitem ||
          '';

      const dwrHours = Number(formData.dwr_hour);

      const payload = {
        task_id: taskId,
        user_id: userId,
        task_name:
          formData.task?.number_str ||
          formData.task?.task_name ||
          '',
        task_date: format(startDate, 'yyyy-MM-dd'),
        submit_status: true,
        task_hour: Number(dwrHours), // Required by the API
        status: 0,
        is_standalone: true,
        remark: formData.description?.trim() || '',
        labourCosts: [
          {
            employee: userId,
            employee_name: employeeName,            
            costitem: costItem,
            hours: dwrHours,
          },
        ],
        equipment: [],
      };

      const { data } = await authAxios.post(
        PRIVATE_ROUTE.CREATE_DWR,
        payload
      );

      if (data?.statusCode !== 200) {
        setErrors((prev) => ({
          ...prev,
          submit:
            data?.message ||
            data?.massage ||
            'Failed to submit Non-Billable DWR.',
        }));
        return;
      }

      onSuccess?.(data.dwr);
      onClose();
    } catch (error) {
      console.error('Failed to create Non-Billable DWR:', error);

      setErrors((prev) => ({
        ...prev,
        submit:
          error?.response?.data?.message ||
          error?.response?.data?.massage ||
          'Failed to submit Non-Billable DWR. Please try again.',
      }));
    }
  };

  const fieldLabelSx = { fontSize: '0.78rem', fontWeight: 600, color: '#555', mb: 0.5 };

  return (
    <>
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: 'hidden', boxShadow: 6 } } }}
      >
        <DateRange
          editableDateInputs
          onChange={(item) => {
            const { startDate: s, endDate: e } = item.selection;
            setDateRange([item.selection]);
            setErrors((err) => ({ ...err, date: undefined }));
            setConflicts([]);
            if (s && e && s.toDateString() !== e.toDateString()) {
              setFormData((f) => ({ ...f, time_from: '', time_to: '' }));
              setErrors((err) => ({ ...err, time_from: undefined, time_to: undefined }));
            }
          }}
          moveRangeOnFirstSelection={false}
          ranges={dateRange}
          rangeColors={[TEAL_DARK]}
          color={TEAL_DARK}
          locale={enUS}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1, borderTop: `1px solid ${TEAL_LIGHT}` }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setCalendarAnchor(null)}
            sx={{ backgroundColor: TEAL_DARK, '&:hover': { backgroundColor: TEAL }, textTransform: 'none', fontWeight: 600 }}
          >
            Done
          </Button>
        </Box>
      </Popover>

      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 560 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EventBusyIcon sx={{ color: '#fff', fontSize: 22 }} />
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}>
                  New Daily Work Report
                </Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                  Submit New DWR
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 2.5 }}>
            <Grid container spacing={2}>
              {/* Employee */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.employee_id ? '#d32f2f' : '#555' }}>
                  Employee <span style={{ color: 'red' }}>*</span>
                </Typography>
                {isAdminOrManager ? (
                  <Autocomplete
                    size="small"
                    options={employees}
                    getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                    value={employees.find((e) => e._id === formData.employee_id) || null}
                    onChange={(_, selected) => handleChange('employee_id', selected ? selected._id : '')}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search employee..."
                        error={!!errors.employee_id}
                        helperText={errors.employee_id}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    value={`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim()}
                    disabled
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333', cursor: 'not-allowed' } }}
                  />
                )}
              </Grid>

              {/* Job */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.job ? '#d32f2f' : '#555' }}>
                  Job <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Autocomplete
                  size="small"
                  options={jobs}
                  getOptionLabel={(option) => option.job_number ?? ''}
                  value={formData.job}
                  onChange={(_, selected) => {
                    if (selected) handleJobChange(selected);
                    else {
                      setFormData((f) => ({ ...f, job: null, task: null }));
                      setFilteredTasks([]);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search or select a job"
                      error={!!errors.job}
                      helperText={errors.job}
                      sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                    />
                  )}
                />
              </Grid>

              {/* Task */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.task ? '#d32f2f' : '#555' }}>
                  Task <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.task}>
                  <Select
                    value={formData.task?._id ?? ''}
                    onChange={(e) => {
                      const selected = filteredTasks.find((t) => t._id === e.target.value);
                      if (selected) {
                        setFormData((f) => ({ ...f, task: selected, cost_item: '' }));
                        fetchTaskCostItems(selected._id);
                      }
                      if (errors.task) setErrors((err) => ({ ...err, task: undefined }));
                    }}
                    displayEmpty
                    disabled={!formData.job}
                    sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  >
                    <MenuItem value="" disabled>
                      {formData.job
                        ? (filteredTasks.length ? 'Select a task' : 'No tasks for this job')
                        : 'Select a job first'}
                    </MenuItem>
                    {filteredTasks.map((task) => (
                      <MenuItem key={task._id} value={task._id}>
                        {task.number_str}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.task && <FormHelperText>{errors.task}</FormHelperText>}
                </FormControl>
                <Typography sx={{ ...fieldLabelSx, color: '#555', mt: 1 }}>Task Scope</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.task?.task_scope_id || ''}
                  placeholder="—"
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 0.5, backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { pointerEvents: 'none' } }}
                />
              </Grid>

              {/* Cost Item */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.cost_item ? '#d32f2f' : '#555' }}>
                  Cost Item <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.cost_item}>
                  <Select
                    value={formData.cost_item}
                    onChange={(e) => handleChange('cost_item', e.target.value)}
                    displayEmpty
                    disabled={!formData.task}
                    sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  >
                    <MenuItem value="" disabled>
                      {!formData.task ? 'Select a task first' : taskCostItems.length === 0 ? 'No cost items for this task' : 'Select cost item'}
                    </MenuItem>
                    {taskCostItems.map((item, index) => (
                      <MenuItem
                        key={`${item.costItem}-${index}`}
                        value={item.costItem}
                      >
                        {item.costItem}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.cost_item && <FormHelperText>{errors.cost_item}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Date */}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ ...fieldLabelSx, color: errors.date ? '#d32f2f' : '#555' }}>
                  Task Date <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={dateLabel}
                  onClick={(e) => setCalendarAnchor(e.currentTarget)}
                  inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthIcon sx={{ fontSize: 18, color: TEAL_DARK }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { cursor: 'pointer', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    ...fieldLabelSx,
                    color: errors.dwr_hour ? '#d32f2f' : '#555',
                  }}
                >
                  DWR Hour <span style={{ color: 'red' }}>*</span>
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={formData.dwr_hour}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === '' || /^\d*\.?\d?$/.test(value)) {
                      handleChange('dwr_hour', value);
                    }
                  }}
                  inputProps={{
                    min: 0.1,
                    step: 0.1,
                  }}
                  error={!!errors.dwr_hour}
                  helperText={errors.dwr_hour}
                  placeholder="Enter hours"
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: TEAL,
                    },
                  }}
                />
              </Grid>

              {/* Remark */}
              <Grid item xs={12}>
                <Typography sx={fieldLabelSx}>
                  Remark <span style={{ fontWeight: 400, color: '#999', fontSize: '0.72rem' }}>(optional)</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${TEAL_LIGHT}`,
              backgroundColor: '#fafafa',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ borderColor: TEAL_MID, color: TEAL_DARK, '&:hover': { borderColor: TEAL_DARK, backgroundColor: TEAL_LIGHT } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ backgroundColor: TEAL_DARK, '&:hover': { backgroundColor: TEAL }, fontWeight: 700, px: 3 }}
              >
                Submit
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

NewDailyWorkReport.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  defaultDate: PropTypes.string,
  onSuccess: PropTypes.func,
};